/**
 *
 * RoutePath
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Polyline, withLeaflet } from 'react-leaflet';

import withRest from 'containers/Rest/withRest';
import withRoutes from 'containers/Routes/withEntity';
import withDialog from 'containers/Dialog/withDialog';
import withRoute from 'containers/withRoute';
import { VIEW_ZOOM } from 'components/Map/constants';
import MetaTags from 'components/MetaTags';
import withSettings from 'containers/SettingsProvider/withSettings';
import fitOnMap from 'utils/fitOnMap';
import { routeColor } from 'styles/colors';
import PathMarker from './PathMarker';
import messages from './messages';
import getKmMarkers from './getKmMarkers';

class RoutePath extends React.PureComponent {
  constructor(props) {
    super(props);
    this.reportError = this.reportError.bind(this);
  }

  componentDidMount() {
    this.checkUpdate();
  }

  componentDidUpdate(prevProps) {
    this.checkUpdate(prevProps);
  }

  checkUpdate(prevProps) {
    const { params } = this.props;
    if (params.routeId) {
      this.checkCurrent(prevProps);
      this.checkMapBounds(prevProps);
    }
  }

  /**
   *  Fetch new route if different from currently loaded
   */
  checkCurrent() {
    const { params, currentRoute, apiGet } = this.props;
    if (currentRoute && (currentRoute.loading || currentRoute.error)) {
      return;
    }
    if (currentRoute && Number(currentRoute.id) === Number(params.routeId)) {
      return;
    }
    apiGet(params.routeId, { onError: this.reportError });
  }

  reportError() {
    const { params, dlgAlert, goTo } = this.props;
    const title = { ...messages.notFound, values: { id: params.routeId } };
    dlgAlert({ title, bsStyle: 'danger' }).then(() => goTo('/home'));
  }

  checkMapBounds(prevProps) {
    const { params, currentRoute, leaflet } = this.props;
    if (!currentRoute || currentRoute.loading || currentRoute.error) {
      return;
    }
    if (
      prevProps &&
      prevProps.routes &&
      params === prevProps.params &&
      currentRoute.id === prevProps.currentRoute.id
    ) {
      return;
    }
    fitOnMap(leaflet.map, currentRoute.bounds, { zoom: VIEW_ZOOM });
  }

  renderMeta() {
    const { currentRoute } = this.props;
    const tags = {
      title: messages.title,
      description: currentRoute.description,
    };
    return <MetaTags tags={tags} vars={currentRoute} />;
  }

  render() {
    const { params, currentRoute, workingRoute, leaflet, units } = this.props;
    const editing = workingRoute.legs !== undefined;
    if (!params.routeId) {
      return null;
    }
    if (!currentRoute || currentRoute.id !== Number(params.routeId)) {
      return null;
    }
    if (editing) {
      return (
        <React.Fragment>
          {this.renderMeta()}
          <Polyline
            key={[currentRoute.id, true]}
            color={routeColor}
            positions={currentRoute.points}
            weight={6}
            opacity={0.25}
            interactive={false}
          />
        </React.Fragment>
      );
    }
    const kmMarkers = getKmMarkers(
      currentRoute.points,
      leaflet.map.getZoom(),
      units,
    );
    return (
      <React.Fragment>
        {this.renderMeta()}
        <Polyline
          key={[currentRoute.id, false]} // need different key to force redrawing
          color={routeColor}
          positions={currentRoute.points}
        />
        <PathMarker type="finish" position={currentRoute.finish} />
        <PathMarker
          type="start"
          position={currentRoute.start}
          zIndexOffset={100}
        />
        {kmMarkers}
      </React.Fragment>
    );
  }
}

RoutePath.propTypes = {
  params: PropTypes.object.isRequired,
  apiGet: PropTypes.func.isRequired,
  currentRoute: PropTypes.object,
  workingRoute: PropTypes.object,
  leaflet: PropTypes.object.isRequired,
  dlgAlert: PropTypes.func.isRequired,
  goTo: PropTypes.func.isRequired,
  units: PropTypes.string.isRequired,
};

export default compose(
  withRoute,
  withSettings(['units']),
  withRoutes,
  withDialog,
  withRest,
  withLeaflet,
)(RoutePath);
